class RecommendedProducts extends HTMLElement {
  static SELECTORS = {
    content: '[data-recommendations]',
    slide: '[data-slide]',
    heading: '[data-recommended-heading]',
    subheading: '[data-recommended-subheading]',
    sliderWrapper: '[data-slider-wrapper]',
    prev: '[data-prev]',
    next: '[data-next]',
  };

  connectedCallback() {
    this.contentEl = this.querySelector(RecommendedProducts.SELECTORS.content);
    if (!this.contentEl) return;

    const { sectionId, productId, limit } = this.dataset;
    if (!sectionId || !productId) return;

    this.complementaryTitle = this.dataset.complementaryTitle || '';
    this.complementarySubheading = this.dataset.complementarySubheading || '';

    const route =
      (window.theme &&
        window.theme.routes &&
        window.theme.routes.productRecommendations) ||
      '/recommendations/products';
    this.baseUrl = `${route}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(
      productId,
    )}&limit=${encodeURIComponent(limit || '4')}`;

    this.fetchAndRender();
  }

  disconnectedCallback() {
    this.removeNavListeners();
  }

  async fetchAndRender() {
    try {
      const complementary = await this.fetchSection(
        `${this.baseUrl}&intent=complementary`,
      );
      if (complementary.hasSlides) {
        this.render(complementary.content, 'complementary');
        return;
      }
      const related = await this.fetchSection(this.baseUrl);
      if (related.hasSlides) {
        this.render(related.content, 'related');
        return;
      }
      this.hideSection();
    } catch (err) {
      console.warn('[recommended-products] fetch failed', err);
      this.hideSection();
    }
  }

  async fetchSection(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const text = await response.text();
    const wrapper = document.createElement('div');
    wrapper.innerHTML = text;
    const content = wrapper.querySelector(
      RecommendedProducts.SELECTORS.content,
    );
    const hasSlides =
      !!content && !!content.querySelector(RecommendedProducts.SELECTORS.slide);
    return { content, hasSlides };
  }

  render(content, intent) {
    this.contentEl.innerHTML = content.innerHTML;
    this.dataset.intent = intent;

    if (intent === 'complementary') {
      const heading = this.contentEl.querySelector(
        RecommendedProducts.SELECTORS.heading,
      );
      const subheading = this.contentEl.querySelector(
        RecommendedProducts.SELECTORS.subheading,
      );
      if (heading && this.complementaryTitle)
        heading.textContent = this.complementaryTitle;
      if (subheading && this.complementarySubheading) {
        subheading.textContent = this.complementarySubheading;
      }
    }

    // Theme's animation system keeps `.animation .animation--list-slider` children at
    // opacity 0 until the host receives `.is-visible` (normally toggled by an IntersectionObserver
    // wired up in the registered section handler). Since this section opts out of that handler,
    // trigger the reveal ourselves on the next frame so the transition still plays.
    requestAnimationFrame(() => this.classList.add('is-visible'));

    this.initSlider();
  }

  initSlider() {
    this.sliderWrapper = this.contentEl.querySelector(
      RecommendedProducts.SELECTORS.sliderWrapper,
    );
    if (!this.sliderWrapper) return;

    this.prevBtn = this.querySelector(RecommendedProducts.SELECTORS.prev);
    this.nextBtn = this.querySelector(RecommendedProducts.SELECTORS.next);

    if (this.prevBtn && this.nextBtn) {
      this.onPrev = () => this.scrollBySlide(-1);
      this.onNext = () => this.scrollBySlide(1);
      this.prevBtn.addEventListener('click', this.onPrev);
      this.nextBtn.addEventListener('click', this.onNext);
    }

    this.updateNavState = () => this.refreshNavState();
    this.sliderWrapper.addEventListener('scroll', this.updateNavState, {
      passive: true,
    });
    window.addEventListener('resize', this.updateNavState);
    this.refreshNavState();
  }

  scrollBySlide(direction) {
    const firstSlide = this.sliderWrapper.querySelector(
      RecommendedProducts.SELECTORS.slide,
    );
    if (!firstSlide) return;
    const slideWidth = firstSlide.getBoundingClientRect().width;
    const styles = getComputedStyle(this.sliderWrapper);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    this.sliderWrapper.scrollBy({
      left: direction * (slideWidth + gap),
      behavior: 'smooth',
    });
  }

  refreshNavState() {
    if (!this.sliderWrapper) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.sliderWrapper;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }

  removeNavListeners() {
    if (this.prevBtn && this.onPrev)
      this.prevBtn.removeEventListener('click', this.onPrev);
    if (this.nextBtn && this.onNext)
      this.nextBtn.removeEventListener('click', this.onNext);
    if (this.sliderWrapper && this.updateNavState) {
      this.sliderWrapper.removeEventListener('scroll', this.updateNavState);
    }
    if (this.updateNavState)
      window.removeEventListener('resize', this.updateNavState);
  }

  hideSection() {
    this.hidden = true;
  }
}

customElements.define('recommended-products', RecommendedProducts);
